import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CheckOutlined,
  EyeOutlined,
  InboxOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useAuth } from "contexts/AuthContext";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import CopyableText from "components/common/CopyableText";
import { profileService } from "services/profileService";

const toMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  return err?.message || fallback;
};

const valueFrom = (record, keys) => {
  for (const key of keys) {
    const value = key.split(".").reduce((acc, part) => acc?.[part], record);
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
};

const formatText = (value, fallback = "-") => {
  if (value === undefined || value === null || value === "") return fallback;
  return String(value);
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = dayjs(value);
  return date.isValid() ? date.format("MMM D, YYYY h:mm A") : String(value);
};

const statusColor = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (["APPROVED", "COMPLETED", "CONTACTED", "SCHEDULED"].includes(normalized)) return "green";
  if (["PENDING", "NEW", "REQUESTED", "IN_REVIEW"].includes(normalized)) return "gold";
  if (["DECLINED", "CANCELLED", "REJECTED", "FAILED"].includes(normalized)) return "red";
  return "blue";
};

const formatStatusLabel = (status) =>
  formatText(status)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getRequestId = (record) =>
  valueFrom(record, ["id", "demoRequestId", "requestId", "uuid"]);

const getWorkspaceId = (record) =>
  valueFrom(record, ["workspaceId", "workspace.id"]);

const getRequesterName = (record) =>
  valueFrom(record, [
    "name",
    "fullName",
    "requesterName",
    "customerName",
    "contactName",
    "user.fullName",
  ]);

const getEmail = (record) =>
  valueFrom(record, ["email", "emailAddress", "contactEmail", "requesterEmail", "user.email"]);

const getPhone = (record) =>
  valueFrom(record, ["phone", "phoneNumber", "contactPhone", "requesterPhone", "user.phone"]);

const getOrganization = (record) =>
  valueFrom(record, ["organizationName", "companyName", "businessName", "organization.name"]);

const getProduct = (record) =>
  valueFrom(record, [
    "product",
    "productName",
    "productCode",
    "requestedProduct",
    "demoProduct",
    "product.name",
    "product.code",
  ]);

const getCreatedAt = (record) =>
  valueFrom(record, ["createdAt", "createdDate", "requestedAt", "submittedAt", "dateCreated"]);

const getMessage = (record) =>
  valueFrom(record, ["message", "note", "notes", "description", "comment", "requestDetails"]);

const getStatus = (record) => valueFrom(record, ["status", "requestStatus", "state"]);

const getSource = (record) => valueFrom(record, ["source", "channel", "origin"]);

const getReadAt = (record) => valueFrom(record, ["readAt", "dateRead", "markedReadAt"]);

const isRead = (record) => {
  const status = String(getStatus(record) || "").toUpperCase();
  return Boolean(
    record?.read ||
      record?.isRead ||
      getReadAt(record) ||
      ["READ", "CONTACTED", "SCHEDULED", "COMPLETED", "CANCELLED"].includes(status),
  );
};

const hasStatus = (record, status) =>
  String(getStatus(record) || "").toUpperCase() === status;

const shortId = (id) => {
  const value = formatText(id, "");
  return value.length > 14 ? `${value.slice(0, 8)}...${value.slice(-4)}` : value;
};

export default function DemoRequestsPage() {
  const { user } = useAuth();
  const actorId = user?.id || "";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const fetchPage = useCallback(async (pag = pagination) => {
    setLoading(true);
    setError(null);
    try {
      const product = pag.product ?? productFilter;
      const status = pag.status ?? statusFilter;
      const res = await profileService.demoRequests.list({
        page: pag.current - 1,
        size: pag.pageSize,
        ...(product.trim() ? { product: product.trim() } : {}),
        ...(status ? { status } : {}),
      });
      const content = Array.isArray(res?.content) ? res.content : Array.isArray(res) ? res : [];
      setItems(content);
      setPagination((prev) => ({
        ...prev,
        current: pag.current,
        pageSize: pag.pageSize,
        total: res?.totalElements ?? content.length,
      }));
    } catch (err) {
      setError(toMessage(err, "Failed to load demo requests"));
    } finally {
      setLoading(false);
    }
  }, [pagination, productFilter, statusFilter]);

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((record) => {
      const product = formatText(getProduct(record), "").toLowerCase();
      const status = formatText(getStatus(record), "").toLowerCase();
      if (productFilter.trim() && !product.includes(productFilter.trim().toLowerCase())) {
        return false;
      }
      if (statusFilter && status !== statusFilter.toLowerCase()) {
        return false;
      }
      if (!q) return true;
      return [
        getRequestId(record),
        getEmail(record),
        getProduct(record),
        getStatus(record),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [items, productFilter, query, statusFilter]);

  const statusOptions = useMemo(() => {
    const values = new Set(items.map(getStatus).filter(Boolean).map(String));
    return Array.from(values)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ label: formatStatusLabel(value), value }));
  }, [items]);

  const unreadCount = useMemo(
    () => items.filter((record) => !isRead(record)).length,
    [items],
  );

  const handleFiltersApply = () => {
    fetchPage({ current: 1, pageSize: pagination.pageSize });
  };

  const handleFiltersClear = () => {
    setProductFilter("");
    setStatusFilter("");
    setQuery("");
    fetchPage({ current: 1, pageSize: pagination.pageSize, product: "", status: "" });
  };

  const handleStatusUpdate = async (record, nextStatus, actionKey, successMessage, errorMessage) => {
    const id = getRequestId(record);
    if (!id) return;
    setActionLoading(`${id}:${actionKey}`);
    try {
      const res = await profileService.demoRequests.updateStatus(id, nextStatus, actorId);
      const updatedRecord = res && typeof res === "object"
        ? res
        : { ...record, status: nextStatus };
      setItems((current) =>
        current.map((item) => (getRequestId(item) === id ? { ...item, ...updatedRecord } : item)),
      );
      setSelected((current) =>
        current && getRequestId(current) === id ? { ...current, ...updatedRecord } : current,
      );
      toast.success(successMessage);
    } catch (err) {
      toast.error(toMessage(err, errorMessage));
    } finally {
      setActionLoading(null);
    }
  };

  const renderRequestActions = (record) => {
    const id = getRequestId(record);
    const read = isRead(record);
    const processed = hasStatus(record, "COMPLETED");
    const archived = hasStatus(record, "CANCELLED");

    return (
      <Space size={4}>
        <Tooltip title="View request">
          <Button
            size="small"
            type="primary"
            style={{ backgroundColor: "#111827", borderColor: "#111827" }}
            icon={<EyeOutlined />}
            onClick={() => setSelected(record)}
          />
        </Tooltip>
        <Tooltip title={read ? "Already read" : "Mark as read"}>
          <Button
            size="small"
            type={read ? "default" : "primary"}
            icon={<CheckOutlined />}
            disabled={read}
            loading={actionLoading === `${id}:read`}
            onClick={() =>
              handleStatusUpdate(
                record,
                "CONTACTED",
                "read",
                "Demo request marked as read",
                "Failed to mark demo request as read",
              )
            }
          />
        </Tooltip>
        <Tooltip title={processed ? "Already processed" : "Mark as processed"}>
          <Button
            size="small"
            type={processed ? "default" : "primary"}
            style={
              processed || archived
                ? undefined
                : { backgroundColor: "#00a854", borderColor: "#00a854" }
            }
            icon={<CheckCircleOutlined />}
            disabled={processed || archived}
            loading={actionLoading === `${id}:processed`}
            onClick={() =>
              handleStatusUpdate(
                record,
                "COMPLETED",
                "processed",
                "Demo request marked as processed",
                "Failed to mark demo request as processed",
              )
            }
          />
        </Tooltip>
        <Tooltip title={archived ? "Already archived" : "Archive"}>
          <Button
            size="small"
            type={archived ? "default" : "primary"}
            danger={!archived}
            icon={<InboxOutlined />}
            disabled={archived}
            loading={actionLoading === `${id}:archive`}
            onClick={() =>
              handleStatusUpdate(
                record,
                "CANCELLED",
                "archive",
                "Demo request archived",
                "Failed to archive demo request",
              )
            }
          />
        </Tooltip>
      </Space>
    );
  };

  const columns = [
    {
      title: "ID",
      key: "id",
      width: 170,
      ellipsis: true,
      render: (_, record) => {
        const id = getRequestId(record);
        return (
          <Space size={8}>
            {!isRead(record) ? <span style={{ width: 8, height: 8, borderRadius: 8, background: "#2c7be5", display: "inline-block" }} /> : null}
            {id ? (
              <CopyableText value={id} displayValue={shortId(id)} showTooltip={false} />
            ) : (
              "-"
            )}
          </Space>
        );
      },
    },
    {
      title: "Email",
      key: "email",
      ellipsis: true,
      render: (_, record) => (
        <Typography.Text strong={!isRead(record)} ellipsis>
          {formatText(getEmail(record))}
        </Typography.Text>
      ),
    },
    {
      title: "Product",
      key: "product",
      ellipsis: true,
      render: (_, record) => {
        const product = getProduct(record);
        return product ? <Tag color="geekblue">{formatText(product)}</Tag> : "-";
      },
    },
    {
      title: "Workspace",
      key: "workspaceId",
      width: 170,
      ellipsis: true,
      render: (_, record) => {
        const workspaceId = getWorkspaceId(record);
        return workspaceId ? <CopyableText value={workspaceId} displayValue={shortId(workspaceId)} showTooltip={false} /> : "-";
      },
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (_, record) => {
        const status = getStatus(record);
        return status ? (
          <Tag color={statusColor(status)}>{formatStatusLabel(status)}</Tag>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Date Requested",
      key: "requestedAt",
      width: 180,
      render: (_, record) => formatDate(getCreatedAt(record)),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => renderRequestActions(record),
    },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="clipboard-list"
          title="Demo Requests"
          subtitle={`${pagination.total || filteredItems.length} requests · ${unreadCount} unread`}
          className="mb-2 mt-2 flex-1"
        />
        <Button icon={<ReloadOutlined />} onClick={() => fetchPage()} loading={loading}>
          Refresh
        </Button>
      </div>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap size={12}>
          <Input.Search
            allowClear
            placeholder="Search email, product, status"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            style={{ width: 280 }}
          />
          <Input
            allowClear
            placeholder="Product"
            value={productFilter}
            onChange={(event) => setProductFilter(event.target.value)}
            onPressEnter={handleFiltersApply}
            style={{ width: 220 }}
          />
          <Select
            allowClear
            showSearch
            placeholder="Status"
            value={statusFilter || undefined}
            onChange={(value) => setStatusFilter(value || "")}
            options={statusOptions}
            style={{ width: 190 }}
          />
          <Button type="primary" onClick={handleFiltersApply} loading={loading}>
            Apply
          </Button>
          <Button onClick={handleFiltersClear}>
            Clear
          </Button>
        </Space>
      </Card>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load"
          description={error}
          style={{ marginBottom: 12 }}
          action={<Button size="small" onClick={() => fetchPage()}>Retry</Button>}
        />
      ) : null}

      <Table
        rowKey={(record) => getRequestId(record) || JSON.stringify(record)}
        loading={loading}
        columns={columns}
        dataSource={filteredItems}
        className="custom-table"
        bordered
        tableLayout="fixed"
        scroll={{ x: "max-content" }}
        size="small"
        rowClassName={(record) => (!isRead(record) ? "fw-semibold" : "")}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
        onChange={(pag) => fetchPage({ current: pag.current, pageSize: pag.pageSize })}
        locale={{ emptyText: "No demo requests found." }}
      />

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected ? (
          <div>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {formatText(getProduct(selected), "Demo Request")}
            </Typography.Title>
            <Typography.Text type="secondary">
              {formatText(getEmail(selected))}
            </Typography.Text>
          </div>
        ) : "Demo Request"}
        width={580}
        extra={
          selected ? (
            renderRequestActions(selected)
          ) : null
        }
      >
        {selected ? (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <div
              style={{
                border: "1px solid var(--falcon-border-color)",
                borderRadius: 6,
                padding: 16,
                background: "var(--falcon-emphasis-bg)",
              }}
            >
              <Space wrap size={8}>
                {getStatus(selected) ? (
                  <Tag color={statusColor(getStatus(selected))}>
                    {formatStatusLabel(getStatus(selected))}
                  </Tag>
                ) : null}
                {isRead(selected) ? <Tag color="green">Read</Tag> : <Tag color="gold">Unread</Tag>}
                {getSource(selected) ? <Tag>{formatText(getSource(selected))}</Tag> : null}
              </Space>
              <Typography.Title level={4} style={{ margin: "10px 0 4px" }}>
                {formatText(getProduct(selected), "Demo Request")}
              </Typography.Title>
              <Typography.Text type="secondary">
                Requested {formatDate(getCreatedAt(selected))}
              </Typography.Text>
            </div>

            <Descriptions bordered column={1} size="small" title="Request">
              <Descriptions.Item label="ID">
                {getRequestId(selected) ? <CopyableText value={getRequestId(selected)} /> : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Product">
                {formatText(getProduct(selected))}
              </Descriptions.Item>
              <Descriptions.Item label="Workspace">
                {getWorkspaceId(selected) ? <CopyableText value={getWorkspaceId(selected)} /> : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {getStatus(selected) ? (
                  <Tag color={statusColor(getStatus(selected))}>
                    {formatStatusLabel(getStatus(selected))}
                  </Tag>
                ) : (
                  "-"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Requested">
                {formatDate(getCreatedAt(selected))}
              </Descriptions.Item>
              <Descriptions.Item label="Read At">{formatDate(getReadAt(selected))}</Descriptions.Item>
            </Descriptions>

            <Descriptions bordered column={1} size="small" title="Customer">
              <Descriptions.Item label="Name">
                {formatText(getRequesterName(selected), "Unknown")}
              </Descriptions.Item>
              <Descriptions.Item label="Email">{formatText(getEmail(selected))}</Descriptions.Item>
              <Descriptions.Item label="Phone">{formatText(getPhone(selected))}</Descriptions.Item>
              <Descriptions.Item label="Organization">
                {formatText(getOrganization(selected))}
              </Descriptions.Item>
              <Descriptions.Item label="Source">{formatText(getSource(selected))}</Descriptions.Item>
            </Descriptions>

            <div>
              <Typography.Text strong>Message</Typography.Text>
              <div
                style={{
                  marginTop: 8,
                  padding: 14,
                  minHeight: 88,
                  border: "1px solid var(--falcon-border-color)",
                  borderRadius: 6,
                  background: "var(--falcon-body-bg)",
                  whiteSpace: "pre-wrap",
                }}
              >
                {formatText(getMessage(selected), "No message provided.")}
              </div>
            </div>

            <div>
              <Typography.Text strong>Full JSON</Typography.Text>
              <Typography.Paragraph
                copyable={{ text: JSON.stringify(selected, null, 2) }}
                style={{
                  marginTop: 8,
                  marginBottom: 0,
                  padding: 14,
                  border: "1px solid var(--falcon-border-color)",
                  borderRadius: 6,
                  background: "var(--falcon-gray-100)",
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    maxHeight: 320,
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {JSON.stringify(selected, null, 2)}
                </pre>
              </Typography.Paragraph>
            </div>
          </Space>
        ) : null}
      </Drawer>
    </div>
  );
}
